/**
 * Cookie / analytics consent state (P0-25 scaffold).
 *
 * Two categories:
 *   - necessary: always true (auth session, language preference — strictly needed)
 *   - analytics: user opt-in (PostHog, non-essential Sentry breadcrumbs)
 *
 * PostHog init (P2-16) must gate on `getConsent().analytics === true`.
 * Sentry stays on regardless — we scrub PII and it's necessary for service quality
 * under legitimate-interest basis (see docs/legal/data_flow_map.md).
 */

const STORAGE_KEY = 'finance101.consent.v1';
const EVENT_NAME = 'finance101:consent-changed';

export type ConsentCategory = 'necessary' | 'analytics';

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  decidedAt: string; // ISO timestamp of the user's last decision
}

export function getConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.necessary !== true || typeof parsed.analytics !== 'boolean') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(analytics: boolean): ConsentState {
  const next: ConsentState = {
    necessary: true,
    analytics,
    decidedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
  return next;
}

export function onConsentChange(listener: (state: ConsentState) => void): () => void {
  const handler = (e: Event) => listener((e as CustomEvent<ConsentState>).detail);
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

export function isDecided(): boolean {
  return getConsent() !== null;
}
